import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { ChangeOrder, Project } from '../types'
import { fmtDate, fmtMoney, todayISO } from '../lib/format'
import { confirmAction } from '../lib/confirm'
import Badge, { type BadgeColor } from '../components/Badge'

export default function ChangeOrdersTab({ project }: { project: Project }) {
  const [cos, setCos] = useState<ChangeOrder[]>([])
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState<Partial<ChangeOrder>>({})

  useEffect(() => {
    api.changeOrders.list(project.id).then(setCos)
  }, [project.id])

  async function add() {
    if (!draft.description) return
    const co = await api.changeOrders.create(project.id, {
      ...draft,
      cost_impact: Number(draft.cost_impact) || 0,
      schedule_impact_days: Number(draft.schedule_impact_days) || 0,
      date: todayISO(),
    })
    setCos((p) => [...p, co])
    setAdding(false)
    setDraft({})
  }

  async function setSigned(co: ChangeOrder) {
    if (
      !confirmAction(
        `Mark CO-${String(co.co_number).padStart(2, '0')} as signed and approved?\n\n` +
          `Description: ${co.description}\n` +
          `Cost impact: ${fmtMoney(co.cost_impact)}\n\n` +
          `Once signed, the CO is binding. The audit log captures this action.`
      )
    )
      return
    const updated = await api.changeOrders.update(co.id, {
      client_signed: 1 as const,
      status: 'approved' as const,
    })
    setCos((p) => p.map((x) => (x.id === co.id ? updated : x)))
  }

  async function setRejected(co: ChangeOrder) {
    if (
      !confirmAction(
        `Reject CO-${String(co.co_number).padStart(2, '0')}?\n\n${co.description}`
      )
    )
      return
    const updated = await api.changeOrders.update(co.id, { status: 'rejected' as const })
    setCos((p) => p.map((x) => (x.id === co.id ? updated : x)))
  }

  const totalImpact = cos
    .filter((co) => co.status === 'approved')
    .reduce((s, co) => s + co.cost_impact, 0)

  return (
    <section>
      <div className="row-between mb-3">
        <div>
          <h2>Change Orders</h2>
          <p className="muted small">
            {cos.length} total · {cos.filter((c) => c.status === 'approved').length} approved · running impact{' '}
            {fmtMoney(totalImpact)}
          </p>
        </div>
        <button className="btn-primary" onClick={() => setAdding(!adding)}>
          {adding ? 'Cancel' : '+ New CO'}
        </button>
      </div>

      <div className="callout mb-3">
        <strong>Process:</strong> No work performed before client signs. Non-negotiable.
      </div>

      {adding && (
        <div className="card mb-3">
          <label className="form-row">
            <span>Description</span>
            <input
              value={draft.description ?? ''}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              placeholder="Add radiant floor in master bath"
            />
          </label>
          <label className="form-row">
            <span>Cost impact ($)</span>
            <input
              type="number"
              value={draft.cost_impact as number | undefined}
              onChange={(e) => setDraft({ ...draft, cost_impact: parseFloat(e.target.value) || 0 })}
            />
          </label>
          <label className="form-row">
            <span>Schedule impact (days)</span>
            <input
              type="number"
              value={draft.schedule_impact_days as number | undefined}
              onChange={(e) =>
                setDraft({ ...draft, schedule_impact_days: parseInt(e.target.value) || 0 })
              }
            />
          </label>
          <button className="btn-primary" onClick={add}>
            Add CO
          </button>
        </div>
      )}

      {cos.length === 0 && !adding && <p className="muted">No change orders yet.</p>}

      {cos.map((co) => {
        const status: { label: string; color: BadgeColor } =
          co.status === 'approved'
            ? { label: 'APPROVED', color: 'green' }
            : co.status === 'rejected'
              ? { label: 'REJECTED', color: 'gray' }
              : { label: 'PENDING SIGNATURE', color: 'gold' }
        return (
          <div key={co.id} className="card mb-2">
            <div className="row-between">
              <div>
                <div className="row-title">
                  CO-{String(co.co_number).padStart(2, '0')} · {co.description}
                </div>
                <div className="muted small">
                  {fmtDate(co.date)} · cost {fmtMoney(co.cost_impact)} · schedule{' '}
                  {co.schedule_impact_days}d
                </div>
                {co.client_signed_date && (
                  <div className="muted small">Signed {fmtDate(co.client_signed_date)}</div>
                )}
              </div>
              <div className="text-right">
                <Badge color={status.color}>{status.label}</Badge>
              </div>
            </div>
            {co.status === 'pending_client_approval' && (
              <div className="row gap-2 mt-2">
                <button className="btn-primary" onClick={() => setSigned(co)}>
                  Mark Signed
                </button>
                <button className="btn-ghost" onClick={() => setRejected(co)}>
                  Reject
                </button>
              </div>
            )}
          </div>
        )
      })}
    </section>
  )
}
